import React, { useEffect, useRef, useState } from "react";
import { ActionIcon, Group, Modal, Switch } from "@mantine/core";
import styles from "./Header.module.scss";
import {
  IconAlertCircle,
  IconBell,
  IconCheck,
  IconClock,
  IconCopy,
  IconLayoutGrid,
  IconLink,
  IconLogout,
  IconRefresh,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import fireToast from "../../helpers/toaster";
import { clearUserCookies, getUserCookie } from "../../helpers/cookies";
import {
  getProfileEmployeeId,
  getProfileHeaderName,
  getProfileHospitalName,
  getProfileHospitalLogo,
  persistUserProfile,
} from "../../helpers/userProfileStorage";
import { HugeiconsIcon } from "@hugeicons/react";
import { Coins01Icon } from "@hugeicons/core-free-icons";
import { PATHS } from "../../constants/paths";
import Components from "./Components";
import ApiService from "../../services/api";
import { ROLES } from "../../constants/roles";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import { clearAdminViewSession } from "../../helpers/adminAccess";

const HEADER_ROLES_WITH_EXTRA_ACTIONS = new Set(["receptionist", "pharmacist"]);

/** Compact User ID for the profile menu (full value still copyable). */
const formatCompactUserId = (userId) => {
  const raw = String(userId || "").trim();
  if (!raw) return "";
  if (raw.startsWith("UUID-S-") && raw.length > 20) {
    return `UUID-S-…${raw.slice(-8)}`;
  }
  if (raw.startsWith("UUID-C-") && raw.length > 20) {
    return `UUID-C-…${raw.slice(-8)}`;
  }
  if (raw.length > 18) {
    return `${raw.slice(0, 8)}…${raw.slice(-6)}`;
  }
  return raw;
};

const formatRoleLabel = (role) => {
  if (role == null || String(role).trim() === "") return "";
  return String(role)
    .trim()
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const getPathByRole = (role) => {
  switch (role) {
    case ROLES.RECEPTIONIST:
      return PATHS.RECEPTIONIST_DASHBOARD;
    case ROLES.PRE_OPD_MANAGEMENT:
      return PATHS.PRE_OPD_ASSESSMENTS;
    case ROLES.DOCTOR:
      return PATHS.DOCTOR_DASHBOARD;
    case ROLES.FOLLOW_UP_MANAGEMENT:
      return PATHS.FOLLOW_UP_LIST;
    case ROLES.COUNSELLOR:
      return PATHS.COUNSELLOR_DASHBOARD;
    case ROLES.NURSE:
      return PATHS.NURSE_DASHBOARD;
    case ROLES.IPD_MANAGEMENT:
      return PATHS.IPD_DASHBOARD;
    case ROLES.PHARMACIST:
      return PATHS.PHARMACIST_DASHBOARD;
    case ROLES.RADIOLOGIST:
      return PATHS.RADIOLOGIST_DASHBOARD;
    case ROLES.LAB_TEST:
      return PATHS.LAB_TEST_DASHBOARD;
    case ROLES.THERAPIST:
      return PATHS.THERAPIST_DASHBOARD;
    case ROLES.YOGA:
      return PATHS.YOGA_DASHBOARD;
    case ROLES.BILLING_MANAGER:
      return PATHS.BILLING_MANAGER_DASHBOARD;
    case ROLES.DIETICIAN:
      return PATHS.DIETICIAN_DASHBOARD;
    case ROLES.STAFF_MANAGER:
      return PATHS.STAFF_MANAGER_STAFF;
    case ROLES.STORE_MANAGER:
      return PATHS.STORE_MANAGER_DASHBOARD;
    case ROLES.ADMIN:
      return PATHS.ADMIN_DASHBOARD;
    default:
      return null;
  }
};

const AvailabilityToggle = ({ variant }) => {
  const staffId = getUserCookie("STAFF_ID");
  const [availableStatus, setAvailableStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(staffId));
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!staffId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    ApiService.getAvailability(staffId)
      .then((res) => {
        const s = res.data?.data?.availableStatus;
        if (!cancelled && (s === "available" || s === "unavailable")) {
          setAvailableStatus(s);
        }
      })
      .catch(() => {
        fireToast(
          "error",
          "Could not load availability.",
          "bottom-right",
          true,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [staffId]);

  if (!staffId || loading || availableStatus == null) {
    return null;
  }

  const isAvailable = availableStatus === "available";

  const patchAvailability = (next) => {
    setUpdating(true);
    ApiService.updateAvailability(staffId, { availableStatus: next })
      .then(() => {
        setAvailableStatus(next);
        fireToast(
          "success",
          next === "available"
            ? "You are now marked as available."
            : "You are now marked as unavailable.",
          "bottom-right",
          true,
        );
      })
      .catch((err) => {
        fireToast(
          "error",
          err.response?.data?.message || "Failed to update availability",
          "bottom-right",
          true,
        );
      })
      .finally(() => setUpdating(false));
  };

  return (
    <div
      className={
        variant === "dropdown"
          ? styles.availability_dropdown
          : styles.availability_inline
      }
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <Switch
        classNames={{
          label: isAvailable
            ? styles.availability_label_on
            : styles.availability_label_off,
        }}
        checked={isAvailable}
        color="green"
        disabled={updating}
        label={isAvailable ? "Available" : "Unavailable"}
        onChange={(e) => {
          const checked = e.currentTarget.checked;
          patchAvailability(checked ? "available" : "unavailable");
        }}
        size="sm"
      />
    </div>
  );
};

const Header = () => {
  const [openProfile, setOpenProfile] = useState(false);
  const [roles, setRoles] = useState([]);
  const [openSwitchRoleModal, setOpenSwitchRoleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [profileName, setProfileName] = useState(getProfileHeaderName());
  const [employeeId, setEmployeeId] = useState(getProfileEmployeeId());
  const [hospitalName, setHospitalName] = useState(getProfileHospitalName());
  const [hospitalLogo, setHospitalLogo] = useState(getProfileHospitalLogo());
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [doctorMinutes, setDoctorMinutes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    ApiService.getStaffMeProfile()
      .then((res) => {
        if (cancelled) return;
        const profileData = res.data?.data;
        if (profileData) {
          persistUserProfile(profileData);
          if (profileData.personalDetails?.fullName) {
            setProfileName(profileData.personalDetails.fullName);
          } else if (profileData.name) {
            setProfileName(profileData.name);
          }
          if (profileData.staffId) {
            setEmployeeId(profileData.staffId);
          }
          if (profileData.hospital?.name) {
            setHospitalName(profileData.hospital.name);
          }
          const logo =
            profileData.hospital?.logo ||
            profileData.hospital?.logoUrl ||
            profileData.hospital?.hospitalLogo ||
            profileData.hospitalLogo;
          if (logo) {
            setHospitalLogo(logo);
          }
          const pic =
            profileData.profilePictureUrl ||
            profileData.personalDetails?.profilePictureUrl;
          if (pic) {
            setProfilePicUrl(pic);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch staff profile picture", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setAvatarFailed(false);
  }, [profilePicUrl]);

  useEffect(() => {
    setLogoFailed(false);
  }, [hospitalLogo]);
  const userRole = localStorage.getItem("user_role");
  const availabilityInDropdown = HEADER_ROLES_WITH_EXTRA_ACTIONS.has(userRole);
  const isAdminRole =
    userRole === ROLES.ADMIN || userRole === "hospital_admin";
  const isDoctorRole = userRole === ROLES.DOCTOR;

  useEffect(() => {
    if (!isDoctorRole) {
      setDoctorMinutes(null);
      return undefined;
    }
    let cancelled = false;
    ApiService.getDoctorMinutesBalance()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        const coins = Number(
          data?.balance?.wellnessCoins ?? data?.balance?.coins ?? data?.coins,
        );
        setDoctorMinutes({
          coins: Number.isFinite(coins) ? coins : 0,
          label: `${Number.isFinite(coins) ? coins.toLocaleString("en-IN") : 0} coins`,
        });
      })
      .catch(() => {
        if (!cancelled) setDoctorMinutes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isDoctorRole]);

  const handleLogout = () => {
    setOpenProfile(false);
    localStorage.clear();
    clearUserCookies();
    navigate("/login");
    fireToast("success", "Logout successfully!", "bottom-right", true);
  };

  useEffect(() => {
    ApiService.getAvailableStaffRoles()
      .then((res) => {
        setRoles(res.data.data.tokenRoles || []);
      })
      .catch((err) => {
        fireToast(
          "error",
          err.response?.data?.message || "Failed to get available staff roles",
          "bottom-right",
          true,
        );
      });
  }, []);

  const currentRole = localStorage.getItem("user_role") || "";

  const openSwitchRole = () => {
    setOpenProfile(false);
    setSelectedRole("");
    setOpenSwitchRoleModal(true);
  };

  const closeSwitchRole = () => {
    if (loading) return;
    setOpenSwitchRoleModal(false);
    setSelectedRole("");
  };

  const handleSwitchRole = () => {
    if (!selectedRole || selectedRole === currentRole) {
      fireToast(
        "error",
        "Select a different role to switch",
        "bottom-right",
        true,
      );
      return;
    }
    const path = getPathByRole(selectedRole);
    if (!path) {
      fireToast("error", "Role not found", "bottom-right", true);
      return;
    }
    setLoading(true);
    clearAdminViewSession();
    localStorage.setItem("user_role", selectedRole);
    setOpenSwitchRoleModal(false);
    window.location.href = path;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCopyUserId = (e) => {
    e.stopPropagation();
    if (!employeeId || !navigator.clipboard) return;
    navigator.clipboard.writeText(String(employeeId));
    fireToast("success", "User ID copied", "bottom-right", true);
  };

  return (
    <div className={styles.header}>
      <div
        className={styles.left_section}
        onClick={() => navigate(PATHS.ROOT)}
        style={{ cursor: "pointer" }}
      >
        <div className={styles.icon_container}>
          {hospitalLogo && !logoFailed ? (
            <img
              src={hospitalLogo}
              alt="Hospital Logo"
              onError={() => setLogoFailed(true)}
              className={styles.logo_img}
            />
          ) : (
            <IconLayoutGrid />
          )}
        </div>
        {hospitalName && (
          <span className={styles.hospital_name}>{hospitalName}</span>
        )}
      </div>
      <div className={styles.right}>
        <Components role={userRole} />
        {/* <Searchbar
          placeholder="Search anything on page"
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        /> */}
        {!availabilityInDropdown ? (
          <AvailabilityToggle variant="inline" />
        ) : null}
        {isDoctorRole && doctorMinutes ? (
          <div
            className={styles.minutes_badge}
            title={doctorMinutes.label}
            aria-label={`Available coins ${doctorMinutes.label}`}
          >
            <HugeiconsIcon
              icon={Coins01Icon}
              size={20}
              color="#E2A012"
              strokeWidth={1.8}
            />
            <span>{doctorMinutes.coins.toLocaleString("en-IN")}</span>
          </div>
        ) : null}
        <div className={styles.notification}>
          <IconBell />
        </div>
        <div className={styles.profile_container} ref={dropdownRef}>
          <div
            className={styles.profile}
            onClick={() => {
              setOpenProfile(!openProfile);
            }}
          >
            {profilePicUrl && !avatarFailed ? (
              <img
                src={profilePicUrl}
                alt=""
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className={styles.profile_initials}>
                {String(profileName || "?")
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase() || "?"}
              </span>
            )}
          </div>
          {openProfile && (
            <div className={styles.options_container}>
              <div className={styles.profile_dropdown_header}>
                <p className={styles.profile_name}>{profileName}</p>
                {employeeId ? (
                  <button
                    type="button"
                    className={styles.profile_user_id}
                    onClick={handleCopyUserId}
                    title={String(employeeId)}
                  >
                    <span className={styles.profile_user_id_label}>ID</span>
                    <span className={styles.profile_user_id_value}>
                      {formatCompactUserId(employeeId)}
                    </span>
                    <IconCopy size={14} stroke={1.75} aria-hidden />
                  </button>
                ) : null}
              </div>
              {availabilityInDropdown ? (
                <AvailabilityToggle variant="dropdown" />
              ) : null}
              <div className={styles.profile_dropdown_divider} />
              <div
                className={styles.menu_item}
                onClick={() => {
                  navigate(PATHS.PROFILE_BASIC_DETAILS);
                  setOpenProfile(false);
                }}
              >
                <IconUser />
                Basic Profile Details
              </div>
              <div
                className={styles.menu_item}
                onClick={() => {
                  navigate(PATHS.PROFILE_REPORT_ISSUE);
                  setOpenProfile(false);
                }}
              >
                <IconAlertCircle />
                Report an Issue
              </div>
              {isDoctorRole && (
                <div
                  className={styles.menu_item}
                  onClick={() => {
                    navigate(PATHS.DOCTOR_MINUTES_USAGE);
                    setOpenProfile(false);
                  }}
                >
                  <IconClock />
                  Minutes Usage
                </div>
              )}
              {isAdminRole && (
                <>
                  <div
                    className={styles.menu_item}
                    onClick={() => {
                      navigate(PATHS.ADMIN_MINUTES_WALLET);
                      setOpenProfile(false);
                    }}
                  >
                    <IconClock />
                    Minutes Wallet
                  </div>
                  <div
                    className={styles.menu_item}
                    onClick={() => {
                      navigate(PATHS.ADMIN_COIN_MANAGEMENT);
                      setOpenProfile(false);
                    }}
                  >
                    <IconLink />
                    Coin Management
                  </div>
                </>
              )}
              {roles?.length > 1 && (
                <div className={styles.menu_item} onClick={openSwitchRole}>
                  <IconRefresh />
                  Switch Role
                </div>
              )}
              <div
                className={`${styles.menu_item} ${styles.menu_item_logout}`}
                onClick={handleLogout}
              >
                <IconLogout />
                Logout
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        opened={openSwitchRoleModal}
        onClose={closeSwitchRole}
        padding="0px"
        withCloseButton={false}
        size="lg"
        centered
        classNames={{ content: styles.switch_role_modal }}
      >
        <div className={styles.switch_role_container}>
          <Group justify="space-between" align="flex-start">
            <div>
              <p className={styles.switch_role_title}>Switch Role</p>
              <p className={styles.switch_role_subtitle}>
                Choose a role to continue in the hospital portal
              </p>
            </div>
            <ActionIcon variant="light" onClick={closeSwitchRole}>
              <IconX style={{ width: "75%", height: "75%" }} />
            </ActionIcon>
          </Group>

          <div className={styles.role_grid} role="listbox" aria-label="Roles">
            {roles.map((role) => {
              const isCurrent = role === currentRole;
              const isSelected = role === selectedRole;
              return (
                <button
                  key={role}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={[
                    styles.role_tile,
                    isCurrent ? styles.role_tile_current : "",
                    isSelected ? styles.role_tile_selected : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={isCurrent || loading}
                  onClick={() => setSelectedRole(role)}
                >
                  <span className={styles.role_tile_label}>
                    {formatRoleLabel(role)}
                  </span>
                  {isCurrent ? (
                    <span className={styles.role_tile_badge}>Current</span>
                  ) : isSelected ? (
                    <span className={styles.role_tile_check} aria-hidden>
                      <IconCheck size={16} stroke={2.5} />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className={styles.switch_role_actions}>
            <PrimaryButton
              text="Cancel"
              variant="outline"
              onClick={closeSwitchRole}
              disabled={loading}
            />
            <PrimaryButton
              text="Switch"
              loading={loading}
              disabled={!selectedRole || selectedRole === currentRole}
              onClick={handleSwitchRole}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Header;
